import React, { useEffect, useMemo, useState, useCallback } from "react";
import SinglePageContainer from "../../../../../../../Common/SinglePageContainer";
import { Button, Form, message } from "antd";
import MeetingForm from "../../Form";
import { useNavigate, useParams } from "react-router";
import dayjs from "dayjs";
import {
  useMeetingGetById,
  useMeetingUpdate,
} from "../../../../../../../../hooks/api/meetings";
import { useVaadTabs } from "../../../context";
import { RecordingAttachment } from "../../Form/AttachmentsRecordings";
import { ChartAttachment } from "../../Form/AttachmentsCharts";
import { useReadingsDelete } from "../../../../../../../../hooks/api/readings";
import { useRecordingDelete } from "../../../../../../../../hooks/api/recordings";
import { useChartAttachmentsDelete } from "../../../../../../../../hooks/api/charts";

export default function EditMeetingContent() {
  const { id: vaadId, meetingId } = useParams();
  const { vaadData, meetingsSubTab, setActiveTab } = useVaadTabs();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // The sub-tab we return to must match the one we came from. We read it from
  // the persisted context (set by the table when opening this page), NOT from
  // router state — the ":meetingId" route redirects via <Navigate to="edit"
  // replace/>, which drops location.state on the way in. We also must NOT
  // re-derive it from startTime: a meeting can sit in the Future table yet
  // have a start instant already before "now", which would wrongly bounce the
  // user to Past.
  const originSubTab: "future" | "past" =
    meetingsSubTab === "past" ? "past" : "future";

  const meetingGetById = useMeetingGetById(meetingId);
  const meetingUpdate = useMeetingUpdate(meetingId);

  const readingDelete = useReadingsDelete();
  const recordingDelete = useRecordingDelete();
  const chartDelete = useChartAttachmentsDelete();

  // ─── Оригинальные данные (что было загружено с сервера) ───
  const [originalReadings, setOriginalReadings] = useState<any[]>([]);
  const [originalCharts, setOriginalCharts] = useState<ChartAttachment[]>([]);
  const [originalRecordings, setOriginalRecordings] = useState<
    RecordingAttachment[]
  >([]);

  // ─── Текущие данные в форме ───
  const [currentReadings, setCurrentReadings] = useState<any[]>([]);
  const [currentCharts, setCurrentCharts] = useState<ChartAttachment[]>([]);
  const [currentRecordings, setCurrentRecordings] = useState<
    RecordingAttachment[]
  >([]);

  // ─── ID удалённых элементов (для отправки на сервер при save) ───
  const [deletedReadingIds, setDeletedReadingIds] = useState<string[]>([]);
  const [deletedChartIds, setDeletedChartIds] = useState<string[]>([]);
  const [deletedRecordingIds, setDeletedRecordingIds] = useState<string[]>([]);

  const [dataLoaded, setDataLoaded] = useState(false);

  const isPast = useMemo(() => {
    if (!meetingGetById.data?.startTime) return false;
    return dayjs(meetingGetById.data.startTime).isBefore(dayjs());
  }, [meetingGetById.data]);

  // ─── Загрузка данных ───
  useEffect(() => {
    if (meetingGetById.data && !dataLoaded) {
      const meeting = meetingGetById.data;
      const readings = meeting.readings || [];
      const recordings: RecordingAttachment[] = (meeting.recordings || []).map(
        (r: any) => ({
          id: r.id,
          name: r.name,
          type: r.type,
          fileLink: r.fileLink,
          middahs: [{ id: vaadData?.middah?.id }],
        }),
      );
      const charts: ChartAttachment[] = (meeting.charts || []).map(
        (c: any) => ({
          id: c.id,
          name: c.name,
          type: c.type,
          fileLink: c.fileLink,
        }),
      );

      setOriginalCharts(charts);
      setCurrentCharts(charts);
      setOriginalReadings(readings);
      setCurrentReadings(readings);
      setOriginalRecordings(recordings);
      setCurrentRecordings(recordings);

      // Сбрасываем удалённые ID при загрузке новых данных
      setDeletedReadingIds([]);
      setDeletedChartIds([]);
      setDeletedRecordingIds([]);

      form.setFieldsValue({
        name: meeting.name,
        description: meeting.description || undefined,
        // Render the meeting-location wall-clock (startTimeLocal/endTimeLocal),
        // which is timezone-free, so parse it as a plain local dayjs.
        date: meeting.startTimeLocal ? dayjs(meeting.startTimeLocal) : null,
        startTime: meeting.startTimeLocal
          ? dayjs(meeting.startTimeLocal)
          : null,
        endTime: meeting.endTimeLocal ? dayjs(meeting.endTimeLocal) : null,
        country: meeting.country,
        state: meeting.state,
        city: meeting.city,
        address: meeting.address,
        zoomLink: meeting.zoomLink,
        charts: charts,
        selectedReadings: readings.map((r) => ({
          id: r.id,
          name: r.name,
          type: r.type,
        })),
        importFromLibraryReadings: [],
        recordings: recordings,
      });

      setDataLoaded(true);
    }
  }, [meetingGetById.data, form, dataLoaded, vaadData?.middah?.id]);

  const handleCancel = () => {
    setActiveTab("meetings");
    navigate(`/vaadim-list/${vaadId}/view`, {
      state: { meetingsSubTab: originSubTab },
    });
  };

  // ─── Удаление из UI (без API) ───
  const handleReadingsChange = useCallback(
    (readings: any[]) => {
      setCurrentReadings(readings);
      // Находим что удалили: было в original, нет в current
      const removedIds = originalReadings
        .filter((orig) => !readings.some((r) => r.id === orig.id))
        .map((r) => r.id);
      setDeletedReadingIds(removedIds);
    },
    [originalReadings],
  );

  const handleChartsChange = useCallback(
    (charts: ChartAttachment[]) => {
      setCurrentCharts(charts);
      const removedIds = originalCharts
        .filter((orig) => !charts.some((c) => c.id === orig.id))
        .map((c) => c.id);
      setDeletedChartIds(removedIds);
    },
    [originalCharts],
  );

  const handleRecordingsChange = useCallback(
    (recordings: RecordingAttachment[]) => {
      setCurrentRecordings(recordings);
      const removedIds = originalRecordings
        .filter((orig) => !recordings.some((r) => r.id === orig.id))
        .map((r) => r.id);
      setDeletedRecordingIds(removedIds);
    },
    [originalRecordings],
  );

  // ─── Submit: сначала удаляем на сервере, потом обновляем meeting ───
  const handleSubmit = async () => {
    if (!vaadId) {
      message.error("Vaad ID is missing");
      return;
    }
    try {
      const values = await form.validateFields();

      const baseDate = values.date ? dayjs(values.date) : dayjs();
      const startTime = values.startTime ? dayjs(values.startTime) : dayjs();
      const endTime = values.endTime ? dayjs(values.endTime) : dayjs();

      // The picked date/time is wall-clock time at the meeting location, so we
      // resolve the true UTC instant through the meeting's timezone. The tables
      // and form render the location's wall-clock via startTimeLocal/endTimeLocal.
      const timezone =
        meetingGetById.data?.timezone || vaadData?.timezone || dayjs.tz.guess();
      const date = baseDate.format("YYYY-MM-DD");
      const toUtcIso = (time: dayjs.Dayjs) =>
        dayjs.tz(`${date} ${time.format("HH:mm:ss")}`, timezone).toISOString();

      const startDateTime = toUtcIso(startTime);
      const endDateTime = toUtcIso(endTime);

      // ─── Сначала удаляем на сервере всё что удалили в UI ───
      const deletePromises: Promise<any>[] = [];

      deletedReadingIds.forEach((id) => {
        deletePromises.push(readingDelete.fetch(id));
      });
      deletedRecordingIds.forEach((id) => {
        deletePromises.push(recordingDelete.fetch(id));
      });
      deletedChartIds.forEach((id) => {
        deletePromises.push(chartDelete.fetch(id));
      });

      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
      }

      // ─── Потом обновляем meeting ───
      const currentIds = currentReadings.map((r) => r.id);
      const removedReadings = originalReadings.filter(
        (orig) => !currentIds.includes(orig.id),
      );
      const newReadings = values.importFromLibraryReadings || [];

      const payload = {
        name: values.name,
        description: isPast ? values.description : undefined,
        startTime: startDateTime,
        endTime: endDateTime,
        // When editing a past meeting, empty location fields are left out of the
        // payload entirely rather than sent as null/"".
        ...(isPast && !values.country ? {} : { country: values.country }),
        ...(isPast && !values.state ? {} : { state: values.state }),
        ...(isPast && !values.city ? {} : { city: values.city }),
        ...(isPast && !values.address ? {} : { address: values.address }),
        vaad: { id: vaadId },
        importFromLibraryReadings: newReadings,
        recordings: currentRecordings.map((r) => ({
          name: r.name,
          type: r.type,
          fileLink: r.fileLink,
          globalContent: false,
          middahs: [],
        })),
        charts: currentCharts.map((c) => ({
          name: c.name,
          type: c.type,
          fileLink: c.fileLink,
        })),
        importFromLibraryRecordings: [],
      };

      const result = await meetingUpdate.fetch(payload);

      if (result instanceof Error || (result as any)?.isAxiosError) {
        throw result;
      }

      message.success("Meeting updated successfully");
      setActiveTab("meetings");
      navigate(`/vaadim-list/${vaadId}/view`, {
        state: { meetingsSubTab: originSubTab },
      });
    } catch (error) {
      message.error("Failed to update meeting");
    }
  };

  return (
    <SinglePageContainer
      title="Edit Meeting"
      style={{ maxWidth: 792 }}
      cardLoading={meetingGetById.loading}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <MeetingForm
          isPast={isPast}
          initialReadings={currentReadings}
          initialRecordings={currentRecordings}
          initialCharts={currentCharts}
          onReadingsChange={handleReadingsChange}
          onRecordingsChange={handleRecordingsChange}
          onChartsChange={handleChartsChange}
        />
      </Form>
      <div style={{ display: "flex", justifyContent: "end", gap: 12 }}>
        <Button type="primary" ghost onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={meetingUpdate.loading}
        >
          Save
        </Button>
      </div>
    </SinglePageContainer>
  );
}
